import { type ReactNode, useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import api from "../../services/api";
import { AuthContext, type AuthPayloadDto } from "./auth-context";
import socket from "../../services/socket";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthPayloadDto | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isAuthReady, setIsAuthReady] = useState(false);

  useEffect(() => {
    const storedToken = localStorage.getItem("@Blue:token");

    if (storedToken) {
      try {
        const decoded: AuthPayloadDto = jwtDecode(storedToken);
        setUser(decoded);
        setToken(storedToken);
      } catch {
        localStorage.removeItem("@Blue:token");
      }
    }

    setIsAuthReady(true);
  }, []);

  useEffect(() => {
    if (token) {
      socket.auth = { token };
      socket.connect();
      return;
    }

    socket.auth = {};
    socket.disconnect();
  }, [token]);

  const login = (accessToken: string) => {
    const decoded: AuthPayloadDto = jwtDecode(accessToken);

    setUser(decoded);
    setToken(accessToken);

    localStorage.setItem("@Blue:token", accessToken);
  };

  const clearLocalSession = () => {
    setUser(null);
    setToken(null);
    localStorage.clear();
  };

  const logout = async () => {
    const currentToken = token ?? localStorage.getItem("@Blue:token");

    try {
      if (currentToken) {
        await api.post(
          "/auth/logout",
          {},
          {
            headers: {
              Authorization: `Bearer ${currentToken}`,
            },
          },
        );
      }
    } catch {
      // Mesmo com falha na API, o cliente precisa encerrar sessão local.
    } finally {
      clearLocalSession();
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, isAuthReady, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
